import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import InterviewRounds from '@/components/InterviewRounds';
import AdditionalDetails from '@/components/AdditionalDetails';
import {
  ArrowLeft,
  Envelope,
  Phone,
  GraduationCap,
  Briefcase,
  Certificate,
  Translate,
  FileText,
  Buildings,
  UserCircle,
  DownloadSimple,
  Eye,
  X,
  Trash,
  Sparkle,
  CircleNotch,
  Target,
  PencilSimple,
  Check,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CandidateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [matchingLoading, setMatchingLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(false);
  const [roleAppliedFor, setRoleAppliedFor] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  const cvUrl = candidate ? `${API}/candidates/${candidate.id}/cv?token=${encodeURIComponent(token || '')}` : '';

  useEffect(() => {
    fetchCandidate();
    fetchMatchedJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCandidate = async () => {
    try {
      const response = await axios.get(`${API}/candidates/${id}`);
      setCandidate(response.data);
      setRoleAppliedFor(response.data.role_applied_for || '');
    } catch (error) {
      console.error('Error fetching candidate:', error);
      toast.error('Failed to load candidate details');
    } finally {
      setLoading(false);
    }
  };

  const saveRoleAppliedFor = async () => {
    setSavingRole(true);
    try {
      const { data } = await axios.patch(`${API}/candidates/${id}`, {
        role_applied_for: roleAppliedFor.trim(),
      });
      setCandidate(data);
      setRoleAppliedFor(data.role_applied_for || '');
      setEditingRole(false);
      toast.success('Role Applied For updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingRole(false);
    }
  };

  const fetchMatchedJobs = async () => {
    setMatchingLoading(true);
    try {
      const response = await axios.get(`${API}/candidates/${id}/matched-jobs`);
      setMatchedJobs(response.data);
    } catch (error) {
      console.error('Error fetching matched jobs:', error);
      // Silent fail - just don't show the section
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/candidates/${id}`);
      toast.success('Candidate deleted successfully');
      navigate('/candidates');
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Failed to delete candidate');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="skeleton h-96 bg-white border border-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <p className="text-base text-slate-600">Candidate not found</p>
            <Link
              to="/candidates"
              className="inline-block mt-4 btn-primary px-6 py-2 rounded-md text-sm font-medium"
            >
              Back to Candidates
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const InfoSection = ({ icon: Icon, title, content }) => {
    if (!content) return null;

    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="bg-blue-50 text-[#002FA7] p-3 rounded-md">
            <Icon size={24} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {title}
            </h3>
            <p className="text-base text-slate-600 leading-relaxed whitespace-pre-wrap">{content}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="candidate-detail-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/candidates')}
            className="flex items-center space-x-2 text-slate-600 hover:text-[#002FA7] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Candidates</span>
          </button>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-md text-sm font-medium hover:bg-red-50 hover:border-red-300 transition-colors"
            data-testid="delete-candidate-button"
          >
            <Trash size={16} weight="regular" />
            <span>Delete Candidate</span>
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-8 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="bg-gradient-to-br from-[#002FA7] to-[#0284C7] p-4 rounded-lg">
                <UserCircle size={48} weight="duotone" className="text-white" />
              </div>
              <div>
                <h1
                  className="text-4xl font-medium tracking-tight text-slate-900 mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid="candidate-name"
                >
                  {candidate.name || 'Unknown Candidate'}
                </h1>
                {candidate.role && (
                  <p className="text-lg text-slate-600 mb-4">{candidate.role}</p>
                )}

                {/* Role Applied For - inline editable */}
                <div className="mb-4" data-testid="role-applied-for-section">
                  <div className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5">
                    Role Applied For
                  </div>
                  {editingRole ? (
                    <div className="flex items-center space-x-2 max-w-xl">
                      <input
                        type="text"
                        value={roleAppliedFor}
                        onChange={(e) => setRoleAppliedFor(e.target.value.slice(0, 500))}
                        placeholder="e.g. Senior AI Engineer"
                        autoFocus
                        maxLength={500}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRoleAppliedFor();
                          if (e.key === 'Escape') { setEditingRole(false); setRoleAppliedFor(candidate.role_applied_for || ''); }
                        }}
                        className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        data-testid="role-applied-for-input"
                      />
                      <button
                        onClick={saveRoleAppliedFor}
                        disabled={savingRole}
                        className="p-1.5 bg-[#002FA7] text-white rounded hover:bg-[#00227A] disabled:opacity-50 transition-colors"
                        data-testid="role-applied-for-save"
                        title="Save"
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => { setEditingRole(false); setRoleAppliedFor(candidate.role_applied_for || ''); }}
                        disabled={savingRole}
                        className="p-1.5 border border-slate-300 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        data-testid="role-applied-for-cancel"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingRole(true)}
                      className="group flex items-center space-x-2 text-left"
                      data-testid="role-applied-for-edit"
                    >
                      <span className={`text-base ${candidate.role_applied_for ? 'text-slate-800 font-medium' : 'text-slate-400 italic'}`} data-testid="role-applied-for-value">
                        {candidate.role_applied_for || 'Click to add role'}
                      </span>
                      <PencilSimple size={14} className="text-slate-400 group-hover:text-[#002FA7] transition-colors" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-4">
                  {candidate.email && (
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Envelope size={16} />
                      <a href={`mailto:${candidate.email}`} className="hover:text-[#002FA7]">
                        {candidate.email}
                      </a>
                    </div>
                  )}
                  {candidate.phone && (
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Phone size={16} />
                      <a href={`tel:${candidate.phone}`} className="hover:text-[#002FA7]">
                        {candidate.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Matched Roles */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="matched-jobs-section">
          <div className="flex items-center space-x-2 mb-4">
            <Target size={22} weight="duotone" className="text-[#002FA7]" />
            <h2 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Top Matched Roles
            </h2>
            <span className="text-xs font-medium text-[#002FA7] bg-blue-50 px-2 py-0.5 rounded">
              AI-powered
            </span>
          </div>

          {matchingLoading ? (
            <div className="flex items-center space-x-2 py-6 text-slate-600" data-testid="matching-loading">
              <CircleNotch size={20} className="text-[#002FA7] animate-spin" />
              <span className="text-sm">AI is analyzing candidate fit against open roles...</span>
            </div>
          ) : matchedJobs.length === 0 ? (
            <div className="py-6 text-center" data-testid="no-matches">
              <p className="text-sm text-slate-600">
                No matching job descriptions found. <Link to="/jobs/new" className="text-[#002FA7] font-medium hover:underline">Add a job description</Link> to see matches.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="matched-jobs-grid">
              {matchedJobs.map((match, index) => (
                <Link
                  key={match.job.id}
                  to={`/jobs/${match.job.id}`}
                  className="block border border-slate-200 rounded-lg p-5 hover:border-[#002FA7] hover:shadow-md transition-all duration-200"
                  data-testid={`matched-job-${index + 1}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                        index === 0 ? 'bg-[#002FA7] text-white' : 'bg-blue-100 text-[#002FA7]'
                      }`}>
                        #{index + 1}
                      </span>
                      <Briefcase size={18} weight="duotone" className="text-slate-500" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <Sparkle size={14} weight="fill" className="text-[#002FA7]" />
                      <span className="text-sm font-semibold text-[#002FA7]">
                        {Math.round((match.match_score || 0) * 100)}%
                      </span>
                    </div>
                  </div>

                  <h3 className="text-base font-medium text-slate-900 mb-1 line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {match.job.title || 'Untitled Role'}
                  </h3>
                  {match.job.department && (
                    <p className="text-xs text-slate-500 mb-3">{match.job.department}</p>
                  )}

                  {match.match_reason && (
                    <p className="text-sm text-slate-700 leading-relaxed mb-3">
                      {match.match_reason}
                    </p>
                  )}

                  {match.key_strengths && (
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-1.5">
                        Key Strengths
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {match.key_strengths.split(',').slice(0, 4).map((strength, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded"
                          >
                            {strength.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {candidate.summary && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-medium text-slate-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Professional Summary
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">{candidate.summary}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoSection icon={Briefcase} title="Work Experience" content={candidate.work_experience} />
          <InfoSection icon={Buildings} title="Past Companies" content={candidate.past_companies} />
          <InfoSection icon={GraduationCap} title="Education" content={candidate.education} />
          <InfoSection icon={GraduationCap} title="College" content={candidate.college} />
          <InfoSection icon={FileText} title="Skills" content={candidate.skills} />
          <InfoSection icon={Certificate} title="Certifications" content={candidate.certifications} />
          <InfoSection icon={Translate} title="Languages" content={candidate.languages} />
        </div>

        {/* Additional Details */}
        <AdditionalDetails candidate={candidate} onUpdate={setCandidate} />

        {/* Interview Rounds */}
        <div className="mt-6">
          <InterviewRounds candidateId={candidate.id} />
        </div>

        {candidate.cv_file_name && (
          <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6" data-testid="cv-file-section">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <FileText size={32} weight="duotone" className="text-[#002FA7]" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Original CV</p>
                  <p className="text-xs text-slate-500">{candidate.cv_file_name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {candidate.cv_file_name?.toLowerCase().endsWith('.pdf') && (
                  <button
                    onClick={() => setShowPreview(true)}
                    className="flex items-center space-x-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors"
                    data-testid="preview-cv-button"
                  >
                    <Eye size={16} weight="regular" />
                    <span>Preview</span>
                  </button>
                )}
                <a
                  href={cvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white rounded-md text-sm font-medium transition-colors"
                  data-testid="open-cv-button"
                >
                  <DownloadSimple size={16} weight="regular" />
                  <span>Open CV</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* PDF Preview Modal */}
        {showPreview && candidate.cv_file_name?.toLowerCase().endsWith('.pdf') && (
          <div
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
            data-testid="pdf-preview-modal"
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center space-x-3">
                  <FileText size={24} weight="duotone" className="text-[#002FA7]" />
                  <div>
                    <p className="text-base font-medium text-slate-900">{candidate.cv_file_name}</p>
                    <p className="text-xs text-slate-500">{candidate.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                  data-testid="close-preview-button"
                >
                  <X size={20} />
                </button>
              </div>
              <iframe
                src={cvUrl}
                className="flex-1 w-full"
                title={`CV Preview - ${candidate.name}`}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !deleting && setShowDeleteConfirm(false)}
            data-testid="delete-confirm-modal"
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-50 p-3 rounded-lg">
                  <Trash size={24} weight="duotone" className="text-red-600" />
                </div>
                <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Delete Candidate?
                </h3>
              </div>
              <p className="text-base text-slate-600 mb-6">
                Are you sure you want to delete <strong className="text-slate-900">{candidate.name || 'this candidate'}</strong>? This will permanently remove their profile and CV file. This action cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                  data-testid="cancel-delete-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-button"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDetail;